import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import PageHeader from '../components/PageHeader';
import Modal from '../components/Modal';
import SearchableDropdown from '../components/SearchableDropdown';
import TaskAssigneePicker from '../components/TaskAssigneePicker';
import TaskEventProjectFields from '../components/TaskEventProjectFields';
import TagSelector from '../components/TagSelector';
import AttachmentVoidModal from '../components/AttachmentVoidModal';
import { AttachmentVoidPill, AttachmentVoidReason } from '../components/AttachmentVoidNote';
import { useAttachmentVoid } from '../hooks/useAttachmentVoid';
import { isVoided } from '../api/attachmentVoid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare, faTrash, faDownload, faBan, faCircleCheck, faEye, faEyeSlash, faClone, faPlus } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../context/AuthContext';
import {
  fetchTask,
  watchTask,
  unwatchTask,
  createTask,
  createTaskSubtask,
  updateTask,
  updateTaskRiedPriority,
  fetchTaskNotes,
  createTaskNote,
  updateTaskNote,
  deleteTaskNote,
  fetchTaskAttachments,
  fetchTaskActivity,
  fetchTaskSubtasks,
  uploadTaskAttachment,
  taskAttachmentDownloadUrl,
  voidTaskAttachment,
  unvoidTaskAttachment,
  type TaskRow,
  type TaskNoteRow,
  type TaskAttachmentRow,
  type TaskActivityRow,
  type TaskSubtaskRow,
  type TaskAssignee,
  type UpdateTaskPayload,
  type CreateTaskPayload,
} from '../api/tasks';
import { fetchProjectsCategorized, type ProjectRow } from '../api/projects';
import { fetchEvents, type EventRow } from '../api/events';
import { mergeEventChoices } from '../utils/taskEventProject';
import { PROJECT_BOARD_FIRST_COLUMN_ID } from '../constants/projectBoard';
import { fetchVerifiedUsers, ROLE_OPTIONS } from '../api/users';
import { fetchClients } from '../api/clients';
import { TAG_PILL_COLORS, ROLE_LABELS } from '../constants/taskTags';
import { formatTaskDate, isTaskOverdue, toDateInputValue } from '../utils/formatTaskDate';
import { formatDateTimePT } from '../utils/timezone';
import { loadTasksForDependencyPicker } from '../utils/taskDependencyPicker';
import { SortableTh, StaticTh } from '../components/SortableTh';
import { useTableSortState } from '../hooks/useTableSort';
import { sortRows } from '../utils/tableSort';
import { displayTaskPriority, userHasRiedRole } from '../utils/taskPriorityDisplay';
import FileDropZone from '../components/FileDropZone';
import TaskSubtasksEditor from '../components/TaskSubtasksEditor';
import './Page.css';
import './TaskDetail.css';


const STATUS_CLASS: Record<string, string> = {
  'Unassigned': 'task-status-unassigned',
  'To-Do': 'task-status-todo',
  'In Progress': 'task-status-inprogress',
  'On Hold': 'task-status-onhold',
  'Completed': 'task-status-completed',
  'Void': 'task-status-void',
};

/** Local-only subtask draft used when duplicating a task (no task id exists yet). */
interface DraftSubtask {
  key: string;
  title: string;
  assigneeUserId: number | null;
}

let draftSubtaskCounter = 0;
const newDraftSubtask = (): DraftSubtask => ({
  key: `draft-${++draftSubtaskCounter}`,
  title: '',
  assigneeUserId: null,
});

const PRIORITY_CLASS: Record<string, string> = {
  Low: 'task-priority-low',
  Medium: 'task-priority-medium',
  High: 'task-priority-high',
  Urgent: 'task-priority-urgent',
};

const fmt = (d: string) => formatTaskDate(d || null);

const fmtDateTime = (d: string) =>
  d ? formatDateTimePT(d, { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit', second: undefined, hour12: true }) : '—';

function activityEventLabel(type: string): string {
  switch (type) {
    case 'status_changed':
      return 'Status';
    case 'assignees_changed':
      return 'Assignees';
    case 'note_added':
      return 'Note added';
    case 'note_edited':
      return 'Note edited';
    case 'note_deleted':
      return 'Note deleted';
    case 'attachment_added':
      return 'Attachment';
    case 'attachment_voided':
      return 'Attachment voided';
    case 'attachment_unvoided':
      return 'Attachment restored';
    case 'inline_notes_changed':
      return 'Notes field';
    case 'progress_changed':
      return 'Progress';
    case 'subtask_created':
      return 'Subtask added';
    case 'subtask_updated':
      return 'Subtask updated';
    case 'subtask_deleted':
      return 'Subtask removed';
    default:
      return type.replace(/_/g, ' ');
  }
}

const TaskDetail: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { taskId } = useParams<{ taskId: string }>();
  const { user } = useAuth();
  const taskIdNum = taskId ? parseInt(taskId, 10) : null;
  /** Where to return when pressing Back: the page the user came from, else My Tasks. */
  const backTarget = ((): string => {
    const from = (location.state as { from?: string } | null)?.from;
    return typeof from === 'string' && from.startsWith('/') ? from : '/tasks';
  })();
  const goBack = useCallback(() => navigate(backTarget), [navigate, backTarget]);
  const hasRied = useMemo(
    () => Boolean(user?.id) && userHasRiedRole(user?.roles, user?.role),
    [user?.id, user?.roles, user?.role]
  );

  const [task, setTask] = useState<TaskRow | null>(null);
  const [loading, setLoading] = useState(!!taskIdNum);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  // Display state (read-only card)
  const [name, setName] = useState('');
  const [status, setStatus] = useState<TaskRow['status']>('To-Do');
  const [assignedToUserIds, setAssignedToUserIds] = useState<number[]>([]);
  const [assignedToDisplay, setAssignedToDisplay] = useState('');
  const [clientId, setClientId] = useState<number | null>(null);
  const [clientDisplay, setClientDisplay] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [startDate, setStartDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('');
  const [riedPrioritySaving, setRiedPrioritySaving] = useState(false);
  const [progressPercent, setProgressPercent] = useState<number | null>(null);

  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  /** When true, the edit modal is being used to create a duplicate (new task) instead of editing this one. */
  const [duplicateMode, setDuplicateMode] = useState(false);
  /** Subtask drafts shown only in duplicate mode (pre-filled from the source task). */
  const [duplicateSubtasks, setDuplicateSubtasks] = useState<DraftSubtask[]>([]);
  const [editModalTab, setEditModalTab] = useState<'information' | 'subtasks'>('information');
  const [editName, setEditName] = useState('');
  const [editStatus, setEditStatus] = useState<TaskRow['status']>('To-Do');
  const [editAssignedToUserIds, setEditAssignedToUserIds] = useState<number[]>([]);
  const [editAssignerUserId, setEditAssignerUserId] = useState<number | null>(null);
  const [editAssignerDisplay, setEditAssignerDisplay] = useState('');
  const [editClientId, setEditClientId] = useState<number | null>(null);
  const [editClientDisplay, setEditClientDisplay] = useState('');
  const [editTags, setEditTags] = useState<string[]>([]);
  const [editStartDate, setEditStartDate] = useState('');
  const [editDueDate, setEditDueDate] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editPriority, setEditPriority] = useState('');
  const [editProgressPercent, setEditProgressPercent] = useState('');

  const [voidModalOpen, setVoidModalOpen] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [voidSaving, setVoidSaving] = useState(false);
  const [markCompleteSaving, setMarkCompleteSaving] = useState(false);
  const [watchSaving, setWatchSaving] = useState(false);

  const [addNoteModalOpen, setAddNoteModalOpen] = useState(false);
  const [addNoteText, setAddNoteText] = useState('');
  const [addNoteSaving, setAddNoteSaving] = useState(false);

  const [taskNotes, setTaskNotes] = useState<TaskNoteRow[]>([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [editNoteId, setEditNoteId] = useState<number | null>(null);
  const [editNoteText, setEditNoteText] = useState('');
  const [editNoteSaving, setEditNoteSaving] = useState(false);
  const [deleteNoteId, setDeleteNoteId] = useState<number | null>(null);
  const [deleteNoteSaving, setDeleteNoteSaving] = useState(false);

  const [taskAttachments, setTaskAttachments] = useState<TaskAttachmentRow[]>([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const [uploadSaving, setUploadSaving] = useState(false);

  const [taskActivity, setTaskActivity] = useState<TaskActivityRow[]>([]);
  const [loadingActivity, setLoadingActivity] = useState(false);

  const [taskSubtasks, setTaskSubtasks] = useState<TaskSubtaskRow[]>([]);
  const [loadingTaskSubtasks, setLoadingTaskSubtasks] = useState(false);

  const notesSort = useTableSortState();
  const attachmentSort = useTableSortState();
  const sortedTaskNotes = useMemo(
    () =>
      sortRows(taskNotes, notesSort.sortKey, notesSort.sortDir, (n, key) => {
        switch (key) {
          case 'note':
            return n.note ?? '';
          case 'submitted_by':
            return n.submitted_by ?? '';
          case 'created_at':
            return new Date(n.created_at).getTime();
          default:
            return '';
        }
      }),
    [taskNotes, notesSort.sortKey, notesSort.sortDir]
  );
  const sortedAttachments = useMemo(
    () =>
      sortRows(taskAttachments, attachmentSort.sortKey, attachmentSort.sortDir, (a, key) => {
        switch (key) {
          case 'file':
            return a.original_file_name ?? '';
          case 'uploaded_by':
            return a.uploaded_by ?? '';
          case 'created_at':
            return new Date(a.created_at).getTime();
          default:
            return '';
        }
      }),
    [taskAttachments, attachmentSort.sortKey, attachmentSort.sortDir]
  );
  // Voided files stay listed as history, below the ones still in play.
  const orderedAttachments = useMemo(
    () => [...sortedAttachments].sort((a, b) => Number(isVoided(a)) - Number(isVoided(b))),
    [sortedAttachments]
  );

  const sortedTaskActivity = useMemo(
    () =>
      [...taskActivity].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      ),
    [taskActivity]
  );

  const [verifiedUsers, setVerifiedUsers] = useState<{ id: number; username: string }[]>([]);
  const [clientOptions, setClientOptions] = useState<{ id: number; company: string }[]>([]);
  const [projectOptions, setProjectOptions] = useState<ProjectRow[]>([]);
  const [eventOptions, setEventOptions] = useState<EventRow[]>([]);
  const [editProjectId, setEditProjectId] = useState<number | null>(null);
  const [editEventId, setEditEventId] = useState<number | null>(null);
  const [dependencyOptionsEdit, setDependencyOptionsEdit] = useState<TaskRow[]>([]);
  const [editDependsOnTaskId, setEditDependsOnTaskId] = useState<number | null>(null);

  useEffect(() => {
    if (!taskIdNum) return;
    let cancelled = false;
    setLoading(true);
    fetchTask(taskIdNum, user?.id)
      .then((t) => {
        if (cancelled) return;
        if (t) {
          setTask(t);
          setName(t.name);
          setStatus(t.status);
          const ids =
            t.assignees && t.assignees.length > 0
              ? t.assignees.map((a) => a.user_id)
              : t.assigned_to_user_id != null
                ? [t.assigned_to_user_id]
                : [];
          setAssignedToUserIds(ids);
          setAssignedToDisplay(t.assigned_to_name ?? '');
          setClientId(t.client_id ?? null);
          setClientDisplay(t.client_company ?? '');
          setTags(t.tags ?? []);
          setStartDate(toDateInputValue(t.start_date));
          setDueDate(toDateInputValue(t.due_date));
          setDescription(t.description ?? '');
          setPriority(t.priority ?? '');
          setProgressPercent(
            t.progress_percent != null && Number.isFinite(Number(t.progress_percent))
              ? Math.min(100, Math.max(0, Math.round(Number(t.progress_percent))))
              : null
          );
        } else {
          setTask(null);
        }
      })
      .catch(() => { if (!cancelled) setTask(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [taskIdNum, user?.id]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      fetchVerifiedUsers(),
      user?.id ? fetchClients('client', user.id) : fetchClients('client'),
    ])
      .then(([users, clients]) => {
        if (cancelled) return;
        setVerifiedUsers(users.map((u) => ({ id: u.id, username: u.username })));
        setClientOptions(clients.map((c) => ({ id: c.id, company: c.company })));
      })
      .catch(() => {
        if (!cancelled) { setVerifiedUsers([]); setClientOptions([]); }
      });
    return () => { cancelled = true; };
  }, [user?.id]);

  useEffect(() => {
    let cancelled = false;
    if (!user?.id) {
      setProjectOptions([]);
      return;
    }
    fetchProjectsCategorized(user.id)
      .then((data) => {
        if (cancelled) return;
        const tfri = Array.isArray(user.roles) && user.roles.includes('admin');
        const list =
          tfri && Array.isArray(data.all) && data.all.length > 0
            ? data.all
            : data.my ?? [];
        setProjectOptions(list);
      })
      .catch(() => {
        if (!cancelled) setProjectOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id, user?.roles]);

  useEffect(() => {
    let cancelled = false;
    if (!user?.id) {
      setEventOptions([]);
      return;
    }
    fetchEvents(user.id)
      .then((data) => {
        if (!cancelled) setEventOptions(mergeEventChoices(data));
      })
      .catch(() => {
        if (!cancelled) setEventOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  useEffect(() => {
    let cancelled = false;
    if (!user?.id || !taskIdNum) {
      setDependencyOptionsEdit([]);
      return;
    }
    const pid = editModalOpen ? editProjectId : task?.project_id ?? null;
    loadTasksForDependencyPicker(user.id, pid, taskIdNum)
      .then((list) => {
        if (!cancelled) setDependencyOptionsEdit(list);
      })
      .catch(() => {
        if (!cancelled) setDependencyOptionsEdit([]);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id, taskIdNum, editModalOpen, editProjectId, task?.project_id]);

  const mergedDependencyChoices = useMemo(() => {
    const byId = new Map<number, TaskRow>(dependencyOptionsEdit.map((t) => [t.id, t]));
    const depId = task?.depends_on_task_id;
    const depStatus = task?.depends_on_task_status;
    const depTerminal = depStatus === 'Completed' || depStatus === 'Void';
    if (task && depId != null && !byId.has(depId) && !depTerminal) {
      const depName =
        task.depends_on_task_name && String(task.depends_on_task_name).trim() !== ''
          ? task.depends_on_task_name
          : `Task #${depId}`;
      byId.set(depId, {
        id: depId,
        name: depName,
        status: depStatus ?? 'To-Do',
        assigned_to_id: null,
        assigned_to_user_id: null,
        assigned_to_name: null,
        assigner_id: null,
        assigner_name: null,
        client_id: null,
        client_company: null,
        start_date: null,
        due_date: null,
        description: null,
        notes: null,
        priority: null,
        progress_percent: null,
        tags: [],
        created_at: '',
        updated_at: '',
      });
    }
    return Array.from(byId.values())
      .filter((t) => t.status !== 'Completed' && t.status !== 'Void')
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  }, [
    dependencyOptionsEdit,
    task?.depends_on_task_id,
    task?.depends_on_task_name,
    task?.depends_on_task_status,
  ]);

  const mergedProjectChoices = useMemo(() => {
    const byId = new Map<number, ProjectRow>(projectOptions.map((p) => [p.id, p]));
    if (task?.project_id != null && !byId.has(task.project_id)) {
      byId.set(task.project_id, {
        id: task.project_id,
        name: task.project_name ?? `Project #${task.project_id}`,
        project_types: [],
        start_date: null,
        expected_end_date: null,
        created_by_user_id: null,
        created_by_username: null,
        status: 'Active',
        event_location: null,
        event_start_time: null,
        event_end_time: null,
        collaborators: [],
      });
    }
    return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  }, [projectOptions, task?.project_id, task?.project_name]);

  const taskAssigneesKey = useMemo(
    () => (task?.assignees ?? []).map((a) => a.user_id).sort((a, b) => a - b).join(','),
    [task?.assignees]
  );

  const loadTaskSubtasks = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!taskIdNum) return;
      if (!opts?.silent) setLoadingTaskSubtasks(true);
      try {
        const list = await fetchTaskSubtasks(taskIdNum);
        setTaskSubtasks(list);
      } catch {
        setTaskSubtasks([]);
      } finally {
        if (!opts?.silent) setLoadingTaskSubtasks(false);
      }
    },
    [taskIdNum]
  );

  useEffect(() => {
    if (!taskIdNum) return;
    void loadTaskSubtasks();
  }, [taskIdNum, taskAssigneesKey, loadTaskSubtasks]);

  const editModalAssignees = useMemo((): TaskAssignee[] => {
    return editAssignedToUserIds.map((uid) => {
      const fromTask = task?.assignees?.find((a) => a.user_id === uid);
      if (fromTask) return fromTask;
      const u = verifiedUsers.find((x) => x.id === uid);
      return { user_id: uid, name: u?.username ?? `User #${uid}` };
    });
  }, [editAssignedToUserIds, task?.assignees, verifiedUsers]);

  useEffect(() => {
    if (!taskIdNum) return;
    let cancelled = false;
    setLoadingNotes(true);
    fetchTaskNotes(taskIdNum)
      .then((list) => {
        if (!cancelled) setTaskNotes(list);
      })
      .catch(() => { if (!cancelled) setTaskNotes([]); })
      .finally(() => { if (!cancelled) setLoadingNotes(false); });
    return () => { cancelled = true; };
  }, [taskIdNum]);

  useEffect(() => {
    if (!taskIdNum) return;
    let cancelled = false;
    setLoadingAttachments(true);
    fetchTaskAttachments(taskIdNum)
      .then((list) => {
        if (!cancelled) setTaskAttachments(list);
      })
      .catch(() => { if (!cancelled) setTaskAttachments([]); })
      .finally(() => { if (!cancelled) setLoadingAttachments(false); });
    return () => { cancelled = true; };
  }, [taskIdNum]);

  const loadTaskActivity = useCallback(async () => {
    if (!taskIdNum) return;
    setLoadingActivity(true);
    try {
      const rows = await fetchTaskActivity(taskIdNum);
      setTaskActivity(rows);
    } catch {
      setTaskActivity([]);
    } finally {
      setLoadingActivity(false);
    }
  }, [taskIdNum]);

  useEffect(() => {
    void loadTaskActivity();
  }, [loadTaskActivity]);

  const handleAttachmentFiles = async (files: File[]) => {
    const file = files[0];
    if (!file || !taskIdNum) return;
    setUploadSaving(true);
    setMessage('');
    try {
      const created = await uploadTaskAttachment(taskIdNum, file, user?.id);
      setTaskAttachments((prev) => [created, ...prev]);
      setMessage('File uploaded.');
      void loadTaskActivity();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Failed to upload file.');
    } finally {
      setUploadSaving(false);
    }
  };

  const attachmentVoid = useAttachmentVoid<TaskAttachmentRow>({
    label: (a) => a.original_file_name,
    onVoid: async (a, reason) => {
      const updated = await voidTaskAttachment(a.task_id, a.id, reason, user?.id);
      setTaskAttachments((prev) => prev.map((row) => (row.id === a.id ? { ...row, ...updated } : row)));
      void loadTaskActivity();
    },
    onUnvoid: async (a) => {
      const updated = await unvoidTaskAttachment(a.task_id, a.id, user?.id);
      setTaskAttachments((prev) => prev.map((row) => (row.id === a.id ? { ...row, ...updated } : row)));
      void loadTaskActivity();
    },
    onError: setMessage,
  });

  const prefillEditFieldsFromTask = (nameValue: string) => {
    setEditName(nameValue);
    setEditStatus(status);
    setEditAssignedToUserIds([...assignedToUserIds]);
    setEditAssignerUserId(task?.assigner_id ?? null);
    setEditAssignerDisplay(task?.assigner_name ?? '');
    setEditClientId(clientId);
    setEditClientDisplay(clientDisplay);
    setEditTags([...tags]);
    setEditStartDate(startDate);
    setEditDueDate(dueDate);
    setEditDescription(description);
    setEditPriority(priority);
    setEditProgressPercent(
      task?.progress_percent != null && Number.isFinite(Number(task.progress_percent))
        ? String(Math.min(100, Math.max(0, Math.round(Number(task.progress_percent)))))
        : ''
    );
    setEditProjectId(task?.project_id != null ? task.project_id : null);
    setEditEventId(task?.event_id != null ? task.event_id : null);
    setEditDependsOnTaskId(task?.depends_on_task_id != null ? task.depends_on_task_id : null);
  };

  const openDuplicateModal = () => {
    prefillEditFieldsFromTask(`Copy of ${name}`);
    setDuplicateSubtasks(
      taskSubtasks.map((s) => ({
        key: `draft-${++draftSubtaskCounter}`,
        title: s.title,
        assigneeUserId: s.assignee_user_id ?? null,
      }))
    );
    setDuplicateMode(true);
    setMessage('');
    setEditModalTab('information');
    setEditModalOpen(true);
  };

  const subtaskAssigneeOptions = useMemo(
    () =>
      editAssignedToUserIds
        .map((uid) => verifiedUsers.find((u) => u.id === uid))
        .filter((u): u is { id: number; username: string } => !!u),
    [editAssignedToUserIds, verifiedUsers]
  );

  useEffect(() => {
    if (!duplicateMode) return;
    const assignedSet = new Set(editAssignedToUserIds);
    setDuplicateSubtasks((prev) => {
      let changed = false;
      const next = prev.map((s) => {
        if (s.assigneeUserId != null && !assignedSet.has(s.assigneeUserId)) {
          changed = true;
          return { ...s, assigneeUserId: null };
        }
        return s;
      });
      return changed ? next : prev;
    });
  }, [duplicateMode, editAssignedToUserIds]);

  const addDuplicateSubtask = () => setDuplicateSubtasks((prev) => [...prev, newDraftSubtask()]);
  const removeDuplicateSubtask = (key: string) =>
    setDuplicateSubtasks((prev) => prev.filter((s) => s.key !== key));
  const updateDuplicateSubtask = (key: string, patch: Partial<DraftSubtask>) =>
    setDuplicateSubtasks((prev) => prev.map((s) => (s.key === key ? { ...s, ...patch } : s)));

  const openEditModal = () => {
    prefillEditFieldsFromTask(name);
    setDuplicateMode(false);
    setMessage('');
    setEditModalTab('information');
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditModalTab('information');
    setDuplicateMode(false);
    setDuplicateSubtasks([]);
  };

  const toggleEditTag = (role: string) => {
    setEditTags((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    );
  };

  const handleEditSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!taskIdNum || !task) return;
    if (!editName.trim()) {
      setEditModalTab('information');
      setMessage('Name is required.');
      return;
    }
    if (editTags.length === 0) {
      setEditModalTab('information');
      setMessage('Select at least one role tag.');
      return;
    }
    const progressTrim = editProgressPercent.trim();
    let progressPayload: number | null;
    if (progressTrim === '') {
      progressPayload = null;
    } else {
      const pn = parseInt(progressTrim, 10);
      if (!Number.isInteger(pn) || pn < 0 || pn > 100) {
        setEditModalTab('information');
        setMessage('Progress must be a whole number from 0 to 100, or leave blank to clear.');
        return;
      }
      progressPayload = pn;
    }
    setMessage('');
    setSaving(true);
    try {
      if (duplicateMode) {
        const createPayload: CreateTaskPayload = {
          name: editName.trim(),
          status: editStatus,
          assigned_to_user_ids: editAssignedToUserIds,
          tags: editTags,
          start_date: editStartDate.trim() || undefined,
          due_date: editDueDate.trim() || undefined,
          description: editDescription.trim() || undefined,
          priority: editPriority.trim() || undefined,
          progress_percent: progressPayload,
          depends_on_task_id: editDependsOnTaskId,
        };
        if (editAssignerUserId != null) createPayload.assigned_by_user_id = editAssignerUserId;
        if (editClientId != null) createPayload.client_id = editClientId;
        if (editProjectId != null) {
          createPayload.project_id = editProjectId;
        }
        if (editEventId != null) {
          createPayload.event_id = editEventId;
        }
        if (editProjectId != null || editEventId != null) {
          createPayload.project_board_column_id = PROJECT_BOARD_FIRST_COLUMN_ID;
        }
        const created = await createTask(createPayload);

        const validSubtasks = duplicateSubtasks
          .map((s) => ({ ...s, title: s.title.trim() }))
          .filter((s) => s.title !== '');
        if (validSubtasks.length > 0 && user?.id != null) {
          const assignedSet = new Set(editAssignedToUserIds);
          for (let i = 0; i < validSubtasks.length; i++) {
            const s = validSubtasks[i];
            const assignee =
              s.assigneeUserId != null && assignedSet.has(s.assigneeUserId) ? s.assigneeUserId : null;
            try {
              await createTaskSubtask(created.id, {
                user_id: user.id,
                title: s.title,
                assignee_user_id: assignee,
                sort_order: i,
              });
            } catch {
              // Don't fail the whole duplication if a single subtask can't be created.
            }
          }
        }

        closeEditModal();
        navigate(`/tasks/${created.id}`);
        return;
      }

      const prevPid = task.project_id != null ? task.project_id : null;
      const nextPid = editProjectId;
      const projectChanged =
        (prevPid == null && nextPid != null) ||
        (prevPid != null && nextPid == null) ||
        (prevPid != null && nextPid != null && prevPid !== nextPid);

      const prevEid = task.event_id != null ? task.event_id : null;
      const nextEid = editEventId;
      const eventChanged =
        (prevEid == null && nextEid != null) ||
        (prevEid != null && nextEid == null) ||
        (prevEid != null && nextEid != null && prevEid !== nextEid);

      const payload: UpdateTaskPayload = {
        name: editName.trim(),
        status: editStatus,
        assigned_to_user_ids: editAssignedToUserIds,
        assigned_by_user_id: editAssignerUserId,
        client_id: editClientId,
        tags: editTags,
        start_date: editStartDate.trim() || null,
        due_date: editDueDate.trim() || null,
        description: editDescription.trim() || null,
        priority: editPriority.trim() || null,
        progress_percent: progressPayload,
      };
      if (projectChanged) {
        payload.project_id = nextPid;
        if (nextPid != null && !eventChanged) {
          payload.project_board_column_id = PROJECT_BOARD_FIRST_COLUMN_ID;
        }
      }
      if (eventChanged) {
        payload.event_id = nextEid;
        if (nextEid != null && !projectChanged) {
          payload.project_board_column_id = PROJECT_BOARD_FIRST_COLUMN_ID;
        }
      }

      const prevDep = task.depends_on_task_id ?? null;
      const nextDep = editDependsOnTaskId;
      if (nextDep !== prevDep) {
        payload.depends_on_task_id = nextDep;
      }

      if (user?.id) {
        payload.acting_user_id = user.id;
      }

      await updateTask(taskIdNum, payload);
      setName(editName.trim());
      setStatus(editStatus);
      setAssignedToUserIds([...editAssignedToUserIds]);
      setClientId(editClientId);
      setClientDisplay(editClientDisplay);
      setTags(editTags);
      setStartDate(editStartDate);
      setDueDate(editDueDate);
      setDescription(editDescription.trim());
      setPriority(editPriority);
      setProgressPercent(progressPayload);
      closeEditModal();
      setMessage('Task updated successfully.');
      setTimeout(() => setMessage(''), 4000);
      const updated = await fetchTask(taskIdNum, user?.id);
      if (updated) {
        setTask(updated);
        setAssignedToDisplay(updated.assigned_to_name ?? '');
        setDescription(updated.description ?? '');
        const uids =
          updated.assignees && updated.assignees.length > 0
            ? updated.assignees.map((a) => a.user_id)
            : updated.assigned_to_user_id != null
              ? [updated.assigned_to_user_id]
              : [];
        setAssignedToUserIds(uids);
        setProgressPercent(
          updated.progress_percent != null && Number.isFinite(Number(updated.progress_percent))
            ? Math.min(100, Math.max(0, Math.round(Number(updated.progress_percent))))
            : null
        );
      }
      void loadTaskSubtasks();
      void loadTaskActivity();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Failed to update task.');
    } finally {
      setSaving(false);
    }
  };

  const handleMarkCompleted = async () => {
    if (!taskIdNum || !task || status === 'Completed' || status === 'Void') return;
    setMarkCompleteSaving(true);
    setMessage('');
    try {
      await updateTask(taskIdNum, {
        status: 'Completed',
        ...(user?.id ? { acting_user_id: user.id } : {}),
      });
      setStatus('Completed');
      setMessage('Task marked as completed.');
      setTimeout(() => setMessage(''), 4000);
      const updated = await fetchTask(taskIdNum, user?.id);
      if (updated) setTask(updated);
      void loadTaskActivity();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Failed to update task.');
    } finally {
      setMarkCompleteSaving(false);
    }
  };

  const handleVoidConfirm = async () => {
    const reason = voidReason.trim();
    if (!taskIdNum || !reason) return;
    setVoidSaving(true);
    setMessage('');
    try {
      await updateTask(taskIdNum, {
        status: 'Void',
        void_reason: reason,
        ...(user?.id ? { acting_user_id: user.id } : {}),
      });
      setVoidModalOpen(false);
      setVoidReason('');
      setMessage('Task voided.');
      setTimeout(() => setMessage(''), 4000);
      const updated = await fetchTask(taskIdNum, user?.id);
      if (updated) {
        setTask(updated);
        setStatus(updated.status);
        setDescription(updated.description ?? '');
      }
      void loadTaskActivity();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Failed to void task.');
    } finally {
      setVoidSaving(false);
    }
  };

  const handleToggleWatch = async () => {
    if (!taskIdNum || !user?.id || !task || status === 'Void') return;
    const wasWatched = task.is_watched === true;
    setWatchSaving(true);
    setMessage('');
    try {
      if (wasWatched) {
        await unwatchTask(taskIdNum, user.id);
      } else {
        await watchTask(taskIdNum, user.id);
      }
      const updated = await fetchTask(taskIdNum, user.id);
      if (updated) setTask(updated);
      setMessage(wasWatched ? 'Removed from watch list.' : 'Added to watch list.');
      setTimeout(() => setMessage(''), 4000);
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Could not update watch.');
    } finally {
      setWatchSaving(false);
    }
  };

  const handleRiedPriorityChange = async (value: string) => {
    if (!taskIdNum || !user?.id || !task || status === 'Void') return;
    setRiedPrioritySaving(true);
    setMessage('');
    try {
      const pri = value === '' ? null : (value as 'Low' | 'Medium' | 'High' | 'Urgent');
      const updated = await updateTaskRiedPriority(taskIdNum, user.id, pri);
      setTask((prev) => (prev ? { ...prev, ried_priority: updated.ried_priority ?? null } : null));
      setMessage('Ried priority updated.');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Could not update Ried priority.');
    } finally {
      setRiedPrioritySaving(false);
    }
  };

  const handleAddNote = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = addNoteText.trim();
    if (!trimmed || !taskIdNum || !user?.id) return;
    setAddNoteSaving(true);
    setMessage('');
    try {
      const created = await createTaskNote(taskIdNum, { note: trimmed, user_id: user.id });
      setTaskNotes((prev) => [...prev, created]);
      setAddNoteText('');
      setAddNoteModalOpen(false);
      setMessage('Note added. Assigner and assignee have been notified.');
      setTimeout(() => setMessage(''), 4000);
      void loadTaskActivity();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Failed to add note.');
    } finally {
      setAddNoteSaving(false);
    }
  };

  const openEditNoteModal = (note: TaskNoteRow) => {
    setEditNoteId(note.id);
    setEditNoteText(note.note);
    setMessage('');
    setEditNoteSaving(false);
  };

  const handleEditNoteSave = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = editNoteText.trim();
    if (!trimmed || !taskIdNum || editNoteId == null || !user?.id) return;
    setEditNoteSaving(true);
    setMessage('');
    try {
      const updated = await updateTaskNote(taskIdNum, editNoteId, { note: trimmed, user_id: user.id });
      setTaskNotes((prev) => prev.map((n) => (n.id === editNoteId ? updated : n)));
      setEditNoteId(null);
      setEditNoteText('');
      setMessage('Note updated.');
      setTimeout(() => setMessage(''), 4000);
      void loadTaskActivity();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Only the note creator can edit it.');
    } finally {
      setEditNoteSaving(false);
    }
  };

  const handleDeleteNoteConfirm = async () => {
    if (!taskIdNum || deleteNoteId == null || !user?.id) return;
    setDeleteNoteSaving(true);
    setMessage('');
    try {
      await deleteTaskNote(taskIdNum, deleteNoteId, user.id);
      setTaskNotes((prev) => prev.filter((n) => n.id !== deleteNoteId));
      setDeleteNoteId(null);
      setMessage('Note deleted.');
      setTimeout(() => setMessage(''), 4000);
      void loadTaskActivity();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Only the note creator can delete it.');
    } finally {
      setDeleteNoteSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-layout">
          <Sidebar />
          <main className="page-main">
            <div className="page-content"><p className="page-subtitle">Loading task…</p></div>
          </main>
        </div>
      </div>
    );
  }

  if (!taskIdNum || task === null) {
    return (
      <div className="page-container">
        <div className="page-layout">
          <Sidebar />
          <main className="page-main">
            <div className="page-content">
              <p className="page-subtitle">Task not found.</p>
              <button type="button" className="cancel-button" onClick={goBack}>
                Back to Tasks
              </button>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-layout">
        <Sidebar />
        <main className="page-main">
          <PageHeader
            title={task ? `Task #${task.id} Details` : 'Task Details'}
            subtitle="View and update task information"
            onBack={goBack}
            backLabel="Back"
          />
          <div className="page-content">
            {/* ── Read-only info card ── */}
            <div className="task-info-card">
              <div className="task-info-header">
                <div className="task-info-header-top">
                  <h3 className="task-info-section-title" style={{ margin: 0 }}>Task #{task.id}</h3>
                  <div className="task-action-buttons">
                    {status !== 'Completed' && status !== 'Void' && (
                      <button
                        type="button"
                        className="task-action-btn task-action-btn--complete"
                        data-tooltip="Mark as completed"
                        onClick={handleMarkCompleted}
                        disabled={markCompleteSaving}
                        aria-label="Mark task as completed"
                      >
                        <FontAwesomeIcon icon={faCircleCheck} />
                      </button>
                    )}
                    {status !== 'Void' && (
                      <button
                        type="button"
                        className="task-action-btn task-action-btn--void"
                        data-tooltip="Void task"
                        onClick={() => { setVoidReason(''); setVoidModalOpen(true); }}
                        aria-label="Void task"
                      >
                        <FontAwesomeIcon icon={faBan} />
                      </button>
                    )}
                    {user?.id && status !== 'Void' && (
                      <button
                        type="button"
                        className={`task-action-btn task-action-btn--watch${task.is_watched ? ' task-action-btn--watch-on' : ''}`}
                        data-tooltip={task.is_watched ? 'Stop watching' : 'Watch task'}
                        onClick={handleToggleWatch}
                        disabled={watchSaving}
                        aria-label={task.is_watched ? 'Stop watching task' : 'Watch task'}
                      >
                        <FontAwesomeIcon icon={task.is_watched ? faEyeSlash : faEye} />
                      </button>
                    )}
                    <button
                      type="button"
                      className="task-action-btn task-action-btn--duplicate"
                      data-tooltip="Duplicate task"
                      onClick={openDuplicateModal}
                      aria-label="Duplicate task"
                    >
                      <FontAwesomeIcon icon={faClone} />
                    </button>
                    <button
                      type="button"
                      className="task-action-btn task-action-btn--edit"
                      data-tooltip="Edit task"
                      onClick={openEditModal}
                      disabled={status === 'Void'}
                      aria-label="Edit task"
                    >
                      <FontAwesomeIcon icon={faPenToSquare} />
                    </button>
                  </div>
                </div>
                {hasRied && status !== 'Void' && (
                  <div className="task-info-header-ried-row">
                    <div
                      className="task-ried-priority-control"
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      role="presentation"
                    >
                      <label className="task-ried-priority-label" htmlFor="task-detail-ried-priority">
                        Ried priority
                      </label>
                      <select
                        id="task-detail-ried-priority"
                        className="task-ried-priority-select"
                        value={task.ried_priority ?? ''}
                        onChange={(e) => void handleRiedPriorityChange(e.target.value)}
                        disabled={riedPrioritySaving}
                        aria-label="Your Ried priority for this task"
                      >
                        <option value="">Use task priority</option>
                        <option value="Low">Low</option>
                        <option value="Medium">Medium</option>
                        <option value="High">High</option>
                        <option value="Urgent">Urgent</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>

              <div className="task-info-section">
                <div className="task-info-grid">
                  <div className="task-info-item">
                    <span className="task-info-name-value">{name || '—'}</span>
                  </div>
                </div>
              </div>

              <div className="task-info-section">
                <h4 className="task-info-section-title">Status &amp; Assignment</h4>
                <div className="task-info-grid">
                  <div className="task-info-item">
                    <span className="task-info-label">Status</span>
                    <span className="task-status-badges-group">
                      <span className={`task-status-badge ${STATUS_CLASS[status] ?? ''}`}>{status}</span>
                      {task && isTaskOverdue(task) && (
                        <span className="task-status-badge task-status-overdue">Overdue</span>
                      )}
                    </span>
                  </div>
                  <div className="task-info-item">
                    <span className="task-info-label">Assigned To</span>
                    <span className="task-info-value">{assignedToDisplay || '—'}</span>
                  </div>
                  <div className="task-info-item">
                    <span className="task-info-label">Assigned By</span>
                    <span className="task-info-value">{task?.assigner_name ?? '—'}</span>
                  </div>
                  <div className="task-info-item">
                    <span className="task-info-label">Company</span>
                    <span className="task-info-value">{clientDisplay || '—'}</span>
                  </div>
                </div>
              </div>

              <div className="task-info-section">
                <div className="task-tags-project-row">
                  <div className="task-tags-project-row__col">
                    <h4 className="task-info-section-title">Role Tags</h4>
                    <div className="task-tag-pills">
                      {tags.length > 0
                        ? tags.map((r) => (
                            <span
                              key={r}
                              className="task-tag-pill"
                              style={TAG_PILL_COLORS[r] ? { backgroundColor: TAG_PILL_COLORS[r], color: '#1B1C1E' } : undefined}
                            >
                              {ROLE_LABELS[r] ?? r}
                            </span>
                          ))
                        : (
                          <span className="task-info-value">—</span>
                        )}
                    </div>
                  </div>
                  <div className="task-tags-project-row__col">
                    <h4 className="task-info-section-title">Event</h4>
                    {task?.event_id != null && task.event_name ? (
                      <Link to={`/events/${task.event_id}`} className="task-detail-project-link">
                        {task.event_name}
                      </Link>
                    ) : (
                      <span className="task-info-value">—</span>
                    )}
                  </div>
                  <div className="task-tags-project-row__col">
                    <h4 className="task-info-section-title">Project</h4>
                    {task?.project_id != null && task.project_name ? (
                      <Link to={`/projects/${task.project_id}`} className="task-detail-project-link">
                        {task.project_name}
                      </Link>
                    ) : (
                      <span className="task-info-value">—</span>
                    )}
                  </div>
                </div>
                <div className="task-info-item task-info-item--full" style={{ marginTop: '0.75rem' }}>
                  <span className="task-info-label">Depends on</span>
                  <div className="task-info-value">
                    {task?.depends_on_task_id != null && task.depends_on_task_name ? (
                      <Link to={`/tasks/${task.depends_on_task_id}`} className="task-detail-project-link">
                        {task.depends_on_task_name} (#{task.depends_on_task_id})
                      </Link>
                    ) : (
                      '—'
                    )}
                  </div>
                </div>
              </div>

              <div className="task-info-section">
                <h4 className="task-info-section-title">Description</h4>
                <p className="task-info-multiline">{description.trim() ? description : '—'}</p>
              </div>

              <div className="task-info-section">
                <h4 className="task-info-section-title">Subtasks</h4>
                {loadingTaskSubtasks ? (
                  <p className="task-info-value">Loading…</p>
                ) : taskSubtasks.length === 0 ? (
                  <p className="task-info-value">—</p>
                ) : (
                  <ul className="task-subtasks-card-list">
                    {taskSubtasks.map((s) => (
                      <li
                        key={s.id}
                        className={
                          s.is_completed
                            ? 'task-subtasks-card-item task-subtasks-card-item--done'
                            : 'task-subtasks-card-item'
                        }
                      >
                        <span className="task-subtasks-card-done" aria-hidden>
                          {s.is_completed ? '✓' : '○'}
                        </span>
                        <span className="task-subtasks-card-title">{s.title}</span>
                        {s.assignee_name ? (
                          <span className="task-subtasks-card-assignee">{s.assignee_name}</span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                )}
                {status !== 'Void' && user?.id ? (
                  <p className="page-subtitle task-subtasks-card-hint">Edit task to add or change subtasks.</p>
                ) : null}
              </div>

              <div className="task-info-section">
                <h4 className="task-info-section-title">Dates &amp; Priority</h4>
                <div className="task-info-grid">
                  <div className="task-info-item">
                    <span className="task-info-label">Start Date</span>
                    <span className="task-info-value">{fmt(startDate)}</span>
                  </div>
                  <div className="task-info-item">
                    <span className="task-info-label">Due Date</span>
                    <span className="task-info-value">{fmt(dueDate)}</span>
                  </div>
                  <div className="task-info-item">
                    <span className="task-info-label">Priority</span>
                    {(() => {
                      const effective = task ? displayTaskPriority(task, hasRied) : null;
                      if (!effective) {
                        return <span className="task-info-value">—</span>;
                      }
                      return (
                        <div className="task-priority-display-stack">
                          <span className={`task-priority-badge ${PRIORITY_CLASS[effective] ?? ''}`}>{effective}</span>
                          {hasRied && task.ried_priority && (task.priority ?? '') !== '' ? (
                            <span className="task-priority-base-note">
                              Task priority (shared): {task.priority}
                            </span>
                          ) : null}
                        </div>
                      );
                    })()}
                  </div>
                </div>
                {progressPercent != null && (
                  <div className="task-progress-row">
                    <span className="task-info-label">Progress</span>
                    <div className="task-progress-meter-wrap">
                      <progress
                        className="task-progress-meter"
                        value={progressPercent}
                        max={100}
                        aria-label={`Task progress ${progressPercent} percent`}
                      />
                      <span className="task-progress-meter-label">{progressPercent}%</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Notes section — table right under the task card */}
            <div className="task-detail-table-section task-notes-section">
              <div className="task-notes-section-header">
                <div>
                  <h3 className="task-detail-section-title">Notes</h3>
                  <p className="page-subtitle task-notes-section-subtitle">
                    Timestamped entries from the team.
                  </p>
                </div>
                <button
                  type="button"
                  className="task-add-note-button"
                  onClick={() => { setAddNoteText(''); setMessage(''); setAddNoteModalOpen(true); }}
                  disabled={!user?.id}
                >
                  Add note
                </button>
              </div>
              <div className="task-notes-table-wrapper">
                {loadingNotes ? (
                  <p className="task-notes-loading">Loading notes…</p>
                ) : (
                  <table className="task-notes-table">
                    <thead>
                      <tr>
                        <SortableTh label="Note" columnKey="note" sortKey={notesSort.sortKey} sortDir={notesSort.sortDir} onSort={notesSort.toggleSort} />
                        <SortableTh label="Posted by" columnKey="submitted_by" sortKey={notesSort.sortKey} sortDir={notesSort.sortDir} onSort={notesSort.toggleSort} />
                        <SortableTh label="Posted at" columnKey="created_at" sortKey={notesSort.sortKey} sortDir={notesSort.sortDir} onSort={notesSort.toggleSort} />
                        <StaticTh className="task-notes-actions-header">Actions</StaticTh>
                      </tr>
                    </thead>
                    <tbody>
                      {taskNotes.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="task-notes-cell task-notes-empty">No notes.</td>
                        </tr>
                      ) : (
                        sortedTaskNotes.map((n) => (
                          <tr key={n.id}>
                            <td className="task-notes-cell">{n.note}</td>
                            <td className="task-notes-cell">{n.submitted_by ?? '—'}</td>
                            <td className="task-notes-cell">{fmtDateTime(n.created_at)}</td>
                            <td className="task-notes-cell task-notes-actions">
                              <button
                                type="button"
                                className="task-note-action-btn task-note-action-btn--edit"
                                onClick={() => openEditNoteModal(n)}
                                title="Edit note"
                                aria-label="Edit note"
                              >
                                <FontAwesomeIcon icon={faPenToSquare} />
                              </button>
                              <button
                                type="button"
                                className="task-note-action-btn task-note-action-btn--delete"
                                onClick={() => setDeleteNoteId(n.id)}
                                title="Delete note"
                                aria-label="Delete note"
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {/* Attachments section — under notes; any user can upload, all can download */}
            <div className="task-detail-table-section task-notes-section task-attachments-section">
              <div className="task-notes-section-header task-attachments-header-row">
                <h3 className="task-detail-section-title">Attachments</h3>
              </div>
              <FileDropZone
                className="task-attachment-drop-zone"
                disabled={!taskIdNum || uploadSaving}
                hint={uploadSaving ? 'Uploading…' : 'Any file type — one file per upload.'}
                onFiles={(files) => void handleAttachmentFiles(files)}
              />
              <div className="task-notes-table-wrapper">
                {loadingAttachments ? (
                  <p className="task-notes-loading">Loading attachments…</p>
                ) : (
                  <table className="task-notes-table task-attachments-table">
                    <thead>
                      <tr>
                        <SortableTh label="File name" columnKey="file" sortKey={attachmentSort.sortKey} sortDir={attachmentSort.sortDir} onSort={attachmentSort.toggleSort} />
                        <SortableTh label="Uploaded by" columnKey="uploaded_by" sortKey={attachmentSort.sortKey} sortDir={attachmentSort.sortDir} onSort={attachmentSort.toggleSort} />
                        <SortableTh label="Date" columnKey="created_at" sortKey={attachmentSort.sortKey} sortDir={attachmentSort.sortDir} onSort={attachmentSort.toggleSort} />
                        <StaticTh className="task-notes-actions-header">Actions</StaticTh>
                      </tr>
                    </thead>
                    <tbody>
                      {taskAttachments.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="task-notes-cell task-notes-empty">No attachments.</td>
                        </tr>
                      ) : (
                        orderedAttachments.map((a) => {
                          const voided = isVoided(a);
                          return (
                            <tr key={a.id} className={voided ? 'attachment-voided' : undefined}>
                              <td className="task-notes-cell">
                                <span className="attachment-void-name">{a.original_file_name}</span>{' '}
                                {voided ? <AttachmentVoidPill /> : null}
                                <AttachmentVoidReason attachment={a} />
                              </td>
                              <td className="task-notes-cell">{a.uploaded_by ?? '—'}</td>
                              <td className="task-notes-cell">{fmtDateTime(a.created_at)}</td>
                              <td className="task-notes-cell task-notes-actions">
                                <a
                                  href={taskAttachmentDownloadUrl(taskIdNum!, a.id)}
                                  download={a.original_file_name}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="task-note-action-btn task-attachment-download-btn"
                                  title="Download"
                                  aria-label="Download file"
                                >
                                  <FontAwesomeIcon icon={faDownload} />
                                </a>
                                <button
                                  type="button"
                                  className={voided ? 'attachment-unvoid-btn' : 'attachment-void-btn'}
                                  disabled={attachmentVoid.busy}
                                  onClick={() => (voided ? attachmentVoid.unvoid(a) : attachmentVoid.requestVoid(a))}
                                >
                                  {voided ? 'Unvoid' : 'Void'}
                                </button>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            <div className="task-detail-table-section task-notes-section task-activity-section">
              <div className="task-notes-section-header">
                <div>
                  <h3 className="task-detail-section-title">Activity</h3>
                  <p className="page-subtitle task-notes-section-subtitle">
                    Assignee changes, status, notes, attachments, progress, and subtasks.
                  </p>
                </div>
              </div>
              {loadingActivity ? (
                <p className="task-notes-loading">Loading activity…</p>
              ) : taskActivity.length === 0 ? (
                <p className="task-activity-empty">No activity recorded yet.</p>
              ) : (
                <div className="task-notes-table-wrapper">
                  <table className="task-notes-table task-activity-table">
                    <thead>
                      <tr>
                        <StaticTh label="Time" />
                        <StaticTh label="Event" />
                        <StaticTh label="Details" />
                        <StaticTh label="User" />
                      </tr>
                    </thead>
                    <tbody>
                      {sortedTaskActivity.map((a) => (
                        <tr key={a.id}>
                          <td className="task-activity-table-time">{fmtDateTime(a.created_at)}</td>
                          <td>
                            <span className="task-activity-badge">{activityEventLabel(a.event_type)}</span>
                          </td>
                          <td className="task-activity-table-summary">{a.summary}</td>
                          <td className="task-activity-table-user">
                            {a.actor_username ?? (a.actor_user_id != null ? `User #${a.actor_user_id}` : 'System')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {message && (
              <p
                className={`update-message-banner ${message.startsWith('Task updated') ? 'settings-success' : 'create-order-error'}`}
                role="alert"
                style={{ marginTop: '1rem' }}
              >
                {message}
              </p>
            )}

          </div>
        </main>
      </div>

      <Modal isOpen={voidModalOpen} onClose={() => { setVoidModalOpen(false); setVoidReason(''); }} title="Void task" narrow>
        <div className="modal-body">
          <p style={{ margin: '0 0 0.75rem', color: '#1B1C1E' }}>
            Voiding keeps the task for history but marks it inactive. Why is <strong>Task #{task?.id}</strong> being voided?
            Your answer is appended to the task description.
          </p>
          <div className="form-group">
            <label htmlFor="void-reason" className="form-label">Reason *</label>
            <textarea
              id="void-reason"
              className="form-textarea"
              value={voidReason}
              onChange={(e) => setVoidReason(e.target.value)}
              rows={4}
              placeholder="Explain why this task is void…"
              autoFocus
            />
          </div>
        </div>
        <div className="modal-actions">
          <button type="button" className="cancel-button" onClick={() => { setVoidModalOpen(false); setVoidReason(''); }}>Cancel</button>
          <button type="button" className="task-delete-confirm-btn" onClick={handleVoidConfirm} disabled={voidSaving || !voidReason.trim()}>
            {voidSaving ? 'Voiding…' : 'Void task'}
          </button>
        </div>
      </Modal>

      {/* ── Add note modal ── */}
      <Modal isOpen={addNoteModalOpen} onClose={() => setAddNoteModalOpen(false)} title="Add note" narrow>
        <form onSubmit={handleAddNote}>
          <div className="modal-body">
            <div className="form-group">
              <label htmlFor="add-note-text" className="form-label">Note</label>
              <textarea
                id="add-note-text"
                className="form-textarea"
                value={addNoteText}
                onChange={(e) => setAddNoteText(e.target.value)}
                rows={4}
                placeholder="Enter note..."
                autoFocus
              />
            </div>
          </div>
          <div className="modal-actions">
            <button type="button" className="cancel-button" onClick={() => setAddNoteModalOpen(false)}>Cancel</button>
            <button type="submit" className="update-button" disabled={addNoteSaving || !addNoteText.trim() || !user?.id}>
              {addNoteSaving ? 'Saving…' : 'Add note'}
            </button>
          </div>
        </form>
      </Modal>

      {/* ── Edit note modal ── */}
      <Modal isOpen={editNoteId != null} onClose={() => { setEditNoteId(null); setEditNoteText(''); }} title="Edit note" narrow>
        <form onSubmit={handleEditNoteSave}>
          <div className="modal-body">
            <div className="form-group">
              <label htmlFor="edit-note-text" className="form-label">Note</label>
              <textarea
                id="edit-note-text"
                className="form-textarea"
                value={editNoteText}
                onChange={(e) => setEditNoteText(e.target.value)}
                rows={4}
                placeholder="Enter note..."
              />
            </div>
          </div>
          <div className="modal-actions">
            <button type="button" className="cancel-button" onClick={() => { setEditNoteId(null); setEditNoteText(''); }}>Cancel</button>
            <button type="submit" className="update-button" disabled={editNoteSaving || !editNoteText.trim()}>
              {editNoteSaving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </Modal>

      {/* ── Delete note confirmation modal ── */}
      <Modal isOpen={deleteNoteId != null} onClose={() => setDeleteNoteId(null)} title="Delete note" narrow>
        <div className="modal-body">
          <p style={{ margin: 0, color: '#1B1C1E' }}>Are you sure you want to delete this note? This cannot be undone.</p>
        </div>
        <div className="modal-actions">
          <button type="button" className="cancel-button" onClick={() => setDeleteNoteId(null)}>Cancel</button>
          <button type="button" className="task-delete-confirm-btn" onClick={handleDeleteNoteConfirm} disabled={deleteNoteSaving}>
            {deleteNoteSaving ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </Modal>

      {/* ── Edit modal ── */}
      <Modal isOpen={editModalOpen} onClose={closeEditModal} title={duplicateMode ? 'Duplicate Task' : `Edit Task #${task.id}`} wide>
        <form onSubmit={handleEditSave}>
          <div className={`modal-body${user?.id && !duplicateMode ? ' task-edit-modal-body' : ''}`}>
            {user?.id && !duplicateMode ? (
              <div className="task-edit-modal-tabs" role="tablist" aria-label="Edit task sections">
                <button
                  type="button"
                  role="tab"
                  id="edit-task-tab-information-trigger"
                  aria-selected={editModalTab === 'information'}
                  aria-controls="edit-task-tab-information"
                  className={`task-edit-modal-tab${editModalTab === 'information' ? ' task-edit-modal-tab--active' : ''}`}
                  onClick={() => setEditModalTab('information')}
                >
                  Information
                </button>
                <button
                  type="button"
                  role="tab"
                  id="edit-task-tab-subtasks-trigger"
                  aria-selected={editModalTab === 'subtasks'}
                  aria-controls="edit-task-tab-subtasks"
                  className={`task-edit-modal-tab${editModalTab === 'subtasks' ? ' task-edit-modal-tab--active' : ''}`}
                  onClick={() => setEditModalTab('subtasks')}
                >
                  Subtasks
                </button>
              </div>
            ) : null}

            <div
              className="task-edit-modal-tabpanel"
              role={user?.id ? 'tabpanel' : undefined}
              id={user?.id ? 'edit-task-tab-information' : undefined}
              aria-labelledby={user?.id ? 'edit-task-tab-information-trigger' : undefined}
              hidden={user?.id ? editModalTab !== 'information' : undefined}
            >

            <div className="form-group">
              <label htmlFor="edit-task-name" className="form-label">Name *</label>
              <input
                id="edit-task-name"
                type="text"
                className="form-input"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                required={!user?.id}
              />
            </div>

            <div className="form-group">
              <label htmlFor="edit-task-status" className="form-label">Status</label>
              <select
                id="edit-task-status"
                className="form-select"
                value={editStatus}
                onChange={(e) => setEditStatus(e.target.value as TaskRow['status'])}
              >
                <option value="Unassigned">Unassigned</option>
                <option value="To-Do">To-Do</option>
                <option value="In Progress">In Progress</option>
                <option value="On Hold">On Hold</option>
                <option value="Completed">Completed</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Assigned to</label>
              <p className="page-subtitle" style={{ marginTop: 0, marginBottom: '0.5rem' }}>
                Add or remove people assigned to this task.
              </p>
              <TaskAssigneePicker
                users={verifiedUsers}
                value={editAssignedToUserIds}
                onChange={setEditAssignedToUserIds}
                addPlaceholder="Search user to add…"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Assigned By</label>
              <SearchableDropdown
                options={verifiedUsers.map((u) => u.username)}
                value={editAssignerDisplay}
                onChange={(display) => {
                  setEditAssignerDisplay(display);
                  const u = verifiedUsers.find((x) => x.username === display);
                  setEditAssignerUserId(u ? u.id : null);
                }}
                placeholder="Select user (or clear)..."
              />
            </div>

            <div className="form-group">
              <label className="form-label">Company (client)</label>
              <SearchableDropdown
                options={clientOptions.map((c) => c.company)}
                value={editClientDisplay}
                onChange={(display) => {
                  setEditClientDisplay(display);
                  const c = clientOptions.find((x) => x.company === display);
                  setEditClientId(c ? c.id : null);
                }}
                placeholder="Select company..."
              />
            </div>

            <TaskEventProjectFields
              events={eventOptions}
              projects={mergedProjectChoices}
              eventId={editEventId}
              projectId={editProjectId}
              onEventIdChange={setEditEventId}
              onProjectIdChange={setEditProjectId}
              onAssociationChange={() => setEditDependsOnTaskId(null)}
              eventSelectId="edit-task-event"
              projectSelectId="edit-task-project"
            />

            <div className="form-group">
              <label htmlFor="edit-task-depends-on" className="form-label">
                Depends on
              </label>
              <select
                id="edit-task-depends-on"
                className="form-select"
                value={
                  editDependsOnTaskId != null &&
                  mergedDependencyChoices.some((t) => t.id === editDependsOnTaskId)
                    ? String(editDependsOnTaskId)
                    : ''
                }
                onChange={(e) => {
                  const v = e.target.value;
                  setEditDependsOnTaskId(v === '' ? null : parseInt(v, 10));
                }}
              >
                <option value="">— None —</option>
                {mergedDependencyChoices.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} (#{t.id})
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Role Tags <span className="task-edit-required">(at least one required)</span></label>
              <TagSelector
                options={ROLE_OPTIONS}
                labels={ROLE_LABELS}
                selected={editTags}
                onChange={setEditTags}
                colors={TAG_PILL_COLORS}
              />
            </div>

            <div className="form-group">
              <label htmlFor="edit-task-start" className="form-label">Start Date</label>
              <input
                id="edit-task-start"
                type="date"
                className="form-input"
                value={editStartDate}
                onChange={(e) => setEditStartDate(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label htmlFor="edit-task-due" className="form-label">Due Date</label>
              <input
                id="edit-task-due"
                type="date"
                className="form-input"
                value={editDueDate}
                onChange={(e) => setEditDueDate(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label htmlFor="edit-task-description" className="form-label">Description</label>
              <textarea
                id="edit-task-description"
                className="form-textarea"
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                rows={4}
                placeholder="What is this task about?"
              />
            </div>

            <div className="form-group">
              <label htmlFor="edit-task-priority" className="form-label">Priority</label>
              <select
                id="edit-task-priority"
                className="form-select"
                value={editPriority}
                onChange={(e) => setEditPriority(e.target.value)}
              >
                <option value="">—</option>
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Urgent">Urgent</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="edit-task-progress" className="form-label">Progress (%)</label>
              <input
                id="edit-task-progress"
                type="number"
                className="form-input"
                min={0}
                max={100}
                step={1}
                inputMode="numeric"
                placeholder="0–100, blank to clear"
                value={editProgressPercent}
                onChange={(e) => setEditProgressPercent(e.target.value)}
              />
              <p className="page-subtitle" style={{ marginTop: '0.35rem', marginBottom: 0 }}>
                Whole numbers only. Leave blank to hide the progress bar.
              </p>
            </div>

            {duplicateMode && (
              <div className="form-group">
                <label className="form-label">
                  Subtasks <span className="task-edit-optional-label">(optional)</span>
                </label>
                <p className="page-subtitle" style={{ marginTop: 0, marginBottom: '0.65rem' }}>
                  Copied from the original task. Edit, remove, or add steps, and assign each to one of the
                  people in &ldquo;Assigned to&rdquo; above.
                </p>
                {duplicateSubtasks.length > 0 && (
                  <div className="add-task-subtasks-list">
                    {duplicateSubtasks.map((s) => (
                      <div className="add-task-subtask-row" key={s.key}>
                        <input
                          type="text"
                          className="form-input add-task-subtask-title"
                          placeholder="Subtask title…"
                          value={s.title}
                          maxLength={500}
                          onChange={(e) => updateDuplicateSubtask(s.key, { title: e.target.value })}
                          aria-label="Subtask title"
                        />
                        <select
                          className="form-select add-task-subtask-assignee"
                          value={s.assigneeUserId != null ? String(s.assigneeUserId) : ''}
                          onChange={(e) =>
                            updateDuplicateSubtask(s.key, {
                              assigneeUserId: e.target.value === '' ? null : parseInt(e.target.value, 10),
                            })
                          }
                          disabled={subtaskAssigneeOptions.length === 0}
                          aria-label="Subtask assignee"
                          title={subtaskAssigneeOptions.length === 0 ? 'Add people to “Assigned to” first' : undefined}
                        >
                          <option value="">Unassigned</option>
                          {subtaskAssigneeOptions.map((u) => (
                            <option key={u.id} value={String(u.id)}>
                              {u.username}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          className="add-task-subtask-remove"
                          onClick={() => removeDuplicateSubtask(s.key)}
                          aria-label="Remove subtask"
                          title="Remove subtask"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <button type="button" className="add-task-subtask-add-btn" onClick={addDuplicateSubtask}>
                  <FontAwesomeIcon icon={faPlus} style={{ marginRight: '0.4rem' }} aria-hidden />
                  Add subtask
                </button>
              </div>
            )}

            </div>

            {user?.id && taskIdNum && !duplicateMode ? (
              <div
                className="task-edit-modal-tabpanel"
                role="tabpanel"
                id="edit-task-tab-subtasks"
                aria-labelledby="edit-task-tab-subtasks-trigger"
                hidden={editModalTab !== 'subtasks'}
              >
                <TaskSubtasksEditor
                  taskId={taskIdNum}
                  viewerUserId={user.id}
                  isVoid={task.status === 'Void'}
                  assignees={editModalAssignees}
                  subtasks={taskSubtasks}
                  setSubtasks={setTaskSubtasks}
                  reloadSubtasks={loadTaskSubtasks}
                  onActivityRefresh={() => void loadTaskActivity()}
                />
              </div>
            ) : null}

            {message && (
              <p className="create-order-error" role="alert">{message}</p>
            )}

          </div>
          <div className="modal-actions">
            <button type="button" className="cancel-button" onClick={closeEditModal}>Cancel</button>
            <button type="submit" className="update-button" disabled={saving}>
              {duplicateMode
                ? saving ? 'Creating…' : 'Create task'
                : saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </form>
      </Modal>

      <AttachmentVoidModal control={attachmentVoid} />
    </div>
  );
};

export default TaskDetail;
